import type { Source } from '../../common/js/types';
import { getValidToken, requestWithAuth } from '../../common/js/apiUtils';
import { classifyHttpError, safeFetch } from '../../common/js/errors';
import { parseDataStream } from '../../common/js/streamUtils';
export type { ThreadListItem } from '../../common/js/chatApi';
export { fetchThreads, deleteThread, clearThreads } from '../../common/js/chatApi';

export type ThreadHistoryMessage = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	data?: {
		sources?: Source[];
		feedback?: 'upvote' | 'downvote' | null;
	};
};

export type ThreadStateResponse = {
	messages: ThreadHistoryMessage[];
};

export type HydratedThreadMessage = {
	role: 'user' | 'assistant';
	content: string;
	id: string;
	sources?: Source[];
	feedback?: 'upvote' | 'downvote' | null;
};

type SendMessageParams = {
	agentHost: string;
	threadId: string;
	messages: Array<{
		id?: string;
		role: string;
		content: string;
		data?: { promptSource?: 'suggested' | 'organic'; promptKey?: string };
	}>;
	abortSignal?: AbortSignal;
};

function getAgentBaseUrl( agentHost: string, siteId: number ): string {
	const route = window.agentConfig?.agentRoute ?? 'core-agent';
	return `${ agentHost.replace( /\/$/, '' ) }/${ siteId }/${ route }`;
}

export async function fetchThreadState( params: {
	agentHost: string;
	siteId: number;
	threadId: string;
} ): Promise<ThreadStateResponse> {
	const { agentHost, siteId, threadId } = params;
	const url = `${ getAgentBaseUrl( agentHost, siteId ) }/threads/${ threadId }`;
	return requestWithAuth<ThreadStateResponse>( url, { method: 'GET' } );
}

/**
 * Maps normalized thread history messages to hydrated UI messages.
 */
export function mapThreadStateMessages( messages: ThreadHistoryMessage[] ): HydratedThreadMessage[] {
	return messages
		.filter( ( m ) => m.role === 'user' || m.role === 'assistant' )
		.map( ( m ) => ( {
			role: m.role,
			content: m.content,
			id: m.id,
			sources: m.data?.sources,
			feedback: m.data?.feedback,
		} ) );
}


/**
 * Stream event types for the Vercel AI SDK Data Stream Protocol.
 */
export type StreamEvent =
	| { type: 'text'; data: string }
	| { type: 'sources'; data: Source[] }
	| { type: 'messageId'; data: string }
	| { type: 'status'; data: string }
	| { type: 'error'; data: string };

/**
 * Extracts Source objects from the parsed `2:` data stream payload.
 *
 * The server sends `[{"sources": [Source, ...]}]` — an array of tool-call
 * result objects each containing a `sources` key. This helper normalizes
 * that into a flat `Source[]`.
 */
function extractSources( parsed: Array< Record< string, unknown > > ): Source[] {
	const result: Source[] = [];
	for ( const item of parsed ) {
		if ( Array.isArray( item?.sources ) ) {
			result.push( ...( item.sources as Source[] ) );
		} else if ( item?.post_id != null ) {
			result.push( item as unknown as Source );
		}
	}
	return result;
}

/**
 * Extracts a messageId string from a `2:` data payload, if present.
 */
function extractMessageId( parsed: Record< string, unknown >[] ): string | null {
	for ( const item of parsed ) {
		if ( typeof item?.messageId === 'string' ) {
			return item.messageId;
		}
	}
	return null;
}

function extractStatusFromDataEvents( parsed: Record< string, unknown >[] ): string | null {
	for ( const item of parsed ) {
		if ( typeof item?.status === 'string' ) {
			return item.status;
		}
	}
	return null;
}

function streamDataEventToStudentEvents( parsed: Record< string, unknown >[] ): StreamEvent[] {
	const messageId = extractMessageId( parsed );
	if ( messageId ) {
		return [ { type: 'messageId', data: messageId } ];
	}

	const sources = extractSources( parsed );
	if ( sources.length ) {
		return [ { type: 'sources', data: sources } ];
	}

	const status = extractStatusFromDataEvents( parsed );
	if ( status !== null ) {
		return [ { type: 'status', data: status } ];
	}

	return [];
}

async function* parseStudentDataStream(
	reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<StreamEvent, void, unknown> {
	for await ( const event of parseDataStream( reader ) ) {
		if ( event.type === 'text' ) {
			yield event;
			continue;
		}

		for ( const studentEvent of streamDataEventToStudentEvents( event.data ) ) {
			yield studentEvent;
		}
	}
}

/**
 * Sends a message to the /ask endpoint and returns a stream of events.
 */
export default async function sendMessage(
	params: SendMessageParams
): Promise<AsyncGenerator<StreamEvent, void, unknown>> {
	const {
		agentHost,
		threadId,
		messages,
		abortSignal,
	} = params;

	const siteId = window.agentConfig?.siteId;
	const apiUrl = `${ getAgentBaseUrl( agentHost, siteId ) }/ask`;

	// Get a valid JWT token for authentication
	const token = await getValidToken();

	const response = await safeFetch( apiUrl, {
		method: 'POST',
		mode: 'cors',
		signal: abortSignal,
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${ token }`,
		},
		body: JSON.stringify( {
			thread_id: threadId,
			messages,
		} ),
	} );

	if ( ! response.ok ) {
		throw await classifyHttpError( response );
	}

	const reader = response.body?.getReader();
	if ( ! reader ) {
		throw new Error( 'No response body' );
	}

	return parseStudentDataStream( reader );
}

/**
 * Submits user feedback for an assistant message.
 */
export async function submitFeedback( params: {
	agentHost: string;
	threadId: string;
	messageId: string;
	value: 'upvote' | 'downvote';
} ): Promise<void> {
	const {
		agentHost, threadId, messageId, value,
	} = params;
	const siteId = window.agentConfig?.siteId;
	const apiUrl = `${ getAgentBaseUrl( agentHost, siteId ) }/feedback`;

	// Get a valid JWT token for authentication
	const token = await getValidToken();

	const response = await safeFetch( apiUrl, {
		method: 'POST',
		mode: 'cors',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${ token }`,
		},
		body: JSON.stringify( {
			thread_id: threadId,
			message_id: messageId,
			value,
		} ),
	} );

	if ( ! response.ok ) {
		throw await classifyHttpError( response );
	}
}
